import { Editor, TLShapeId, getPointerInfo, stopEventPropagation, useValue } from '@tldraw/tldraw'
import React, { useCallback, useEffect, useRef } from 'react'

export const useEditableText = (editor: Editor, id: TLShapeId, type: string) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const isEditing = useValue('isEditing', () => editor.getEditingShapeId() === id, [editor, id])

  // If the shape is editing but the input element not focused, focus the element
  useEffect(() => {
    const elm = inputRef.current
    if (elm && isEditing && document.activeElement !== elm) {
      elm.focus()
    }
  }, [isEditing])

  const handleFocus = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.select())
  }, [])

  // When the user presses ctrl / meta enter, complete the editing state.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isEditing) return

      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        editor.complete()
      }
    },
    [editor, isEditing],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isEditing) return
      const text = e.currentTarget.value
      editor.updateShapes([{ id, type, props: { text } }])
    },
    [editor, id, type, isEditing],
  )

  const handleInputPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const shape = editor.getShape(id)

      if (!shape) return

      editor.dispatch({
        ...getPointerInfo(e),
        type: 'pointer',
        name: 'pointer_down',
        target: 'shape',
        shape,
      })

      stopEventPropagation(e) // we need to prevent blurring the input
    },
    [editor, id],
  )

  const handleDoubleClick = stopEventPropagation

  return {
    inputRef,
    isEditing,
    handleFocus,
    handleKeyDown,
    handleChange,
    handleInputPointerDown,
    handleDoubleClick,
  }
}
